import * as d3 from "d3";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import "./BarChart.scss";
import { MONTHS } from "@/basic/utils/months";
import { MedicarePrescriptionPaymentPlanEstimateList } from "@/api/axios";

type BarChartPaymentContext = "with" | "without";

interface BarChartDatum {
  readonly month: string;
  readonly fullMonth: string;
  readonly type: BarChartPaymentContext;
  readonly amount: string;
}

export const PaymentBarChart = ({ results }: MedicarePrescriptionPaymentPlanEstimateList) => {
  const { t: translate } = useTranslation();
  const margin = { top: 10, right: 30, bottom: 20, left: 60 };
  const withColor = "#0071B3";
  const withoutColor = "#AEBFCC";

  const width = 800;
  const height = 530;

  const data: BarChartDatum[] = useMemo(() => {
    const withPayments: BarChartDatum[] = results.map(({ estimation }) => {
      const monthKey = MONTHS[new Date(estimation.month).getUTCMonth() + 1];
      const translatedMonth = translate(`MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentCalculator.${monthKey}`);
      return {
        month: translatedMonth.substring(0, 3),
        fullMonth: translatedMonth,
        type: "with",
        amount: estimation.billed_amount.toString(),
      };
    });

    const withoutPayments: BarChartDatum[] = results.map(({ estimation }) => {
      const monthKey = MONTHS[new Date(estimation.month).getUTCMonth() + 1];
      const translatedMonth = translate(`MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentCalculator.${monthKey}`);
      return {
        month: translatedMonth.substring(0, 3),
        fullMonth: translatedMonth,
        type: "without",
        amount: estimation.true_out_of_pocket_amount,
      };
    });

    return [...withPayments, ...withoutPayments];
  }, [results, translate]);

  // setup the graph svg on first render only
  useEffect(() => {
    const fx = d3
      .scaleBand()
      .domain(data.map((d) => d.month))
      .rangeRound([margin.left, width - margin.right])
      .paddingInner(0.1);

    const types = ["without", "with"];

    const xAxis = d3.scaleBand().domain(types).rangeRound([0, fx.bandwidth()]).padding(0.05);

    const colors = d3.scaleOrdinal().domain(types).range([
      withoutColor,
      withColor,
    ]);
    const maxCost = Math.max(...data.map((d) => parseFloat(d.amount)));
    const yAxis = d3
      .scaleLinear()
      .domain([0, maxCost + 0.1 * maxCost])
      .nice()
      .rangeRound([height - margin.bottom - 50, margin.top]);

    document.getElementById("payment-estimations-graph").innerHTML = "";

    const svg = d3
      .select("#payment-estimations-graph")
      .classed("svg-container", true)
      .append("svg")
      .attr("preserveAspectRatio", "xMinYMin meet")
      .attr("viewBox", "0 0 " + width + " " + height)
      .classed("svg-content-responsive", true)
      .attr("class", "barChart")
      .attr("id", "barChart")
      .attr("width", "100%")
      .attr("height", "100%");

    const getXOffset = (d: typeof data[0]) => {
      /* we want the space between the two bars to be consistent. The with bar is
      almost correctly posistioned, but we need to nudge it over slightly
      to sit over top of the month title, which is dependent on the
      number of months that we have loaded in.
      The without bar should just be positioned relative to the with bar. The bars
      are 20px wide, so push the without bar over 25px from the with bar. */
      const withSize = xAxis("with") - 12 / results.length / 2;
      return d.type === "with" ? withSize : withSize - 25;
    };

    // draw the legend
    const legend = svg.append("g");
    legend
      .append("svg:rect")
      .attr("width", 16)
      .attr("height", 16)
      .attr("y", 20)
      .attr("rx", 4)
      .attr("ry", 4)
      .attr("x", width / 2 - 120)
      .attr("fill", withoutColor);

    legend
      .append("text")
      .text(translate("MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentTable.outOfPocket"))
      .attr("x", width / 2 + 20 - 120)
      .attr("y", 31)
      .attr("font-size", 10);

    legend
      .append("svg:rect")
      .attr("width", 16)
      .attr("height", 16)
      .attr("y", 20)
      .attr("rx", 4)
      .attr("ry", 4)
      .attr("x", width / 2 + 150 - 120)
      .attr("fill", withColor);

    legend
      .append("text")
      .text(translate("MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentTable.estimatedBill"))
      .attr("x", width / 2 + 170 - 120)
      .attr("y", 31)
      .attr("font-size", 10);

    // draw the axes
    svg
      .append("g")
      .attr("class", "axisLine")
      .attr("transform", `translate(0, ${height - margin.bottom})`)
      .call(d3.axisBottom(fx).tickSize(0))
      .attr("font-size", 10);

    svg
      .append("g")
      .attr("transform", `translate(${margin.left}, 50)`)
      .attr("class", "axisLine")
      .call(
        d3
          .axisLeft(yAxis)
          .tickSize(-width)
          .tickPadding(10)
          .tickSizeOuter(0)
          .tickFormat((d) => "$" + d),
      )
      .attr("font-size", 10);

    // Add the text label for the Y axis
    svg
      .append("text")
      .attr("font-weight", 600)
      .attr("font-size", 12)
      .attr("x", -(0 + height / 2))
      .attr("y", 0)
      .attr("transform", "rotate(-90)")
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .text(translate("MEDICARE_PRESCRIPTION_PAYMENT_PLAN.paymentTable.monthlyDrugCosts"));

    // @ts-ignore
    svg
      .append("g")
      .attr("id", "bars")
      .selectAll()
      .data(d3.group(data, (d) => d.month))
      .join("g")
      .attr("transform", ([month]) => `translate(${fx(month)}, 0)`)
      // draw all the bars
      .selectAll()
      .data(([_, d]) => d)
      .join("rect")
      .attr("x", getXOffset)
      .attr("y", (d) => yAxis(parseFloat(d.amount)) + 50)
      .attr("width", 20)
      .attr("height", (d) => yAxis(0) - yAxis(parseFloat(d.amount)))
      // @ts-ignore
      .attr("fill", (d) => colors(d.type));

    // @ts-ignore
    svg
      // draw the numbers above each bar
      .append("g")
      .selectAll()
      .data(d3.group(data, (d) => d.month))
      .join("g")
      .attr("transform", ([month]) => `translate(${fx(month) - 3}, 0)`)
      .selectAll()
      .data(([_, d]) => d)
      .join("text")
      .attr("x", (d) => getXOffset(d) + 2)
      .attr("y", (d) => yAxis(parseFloat(d.amount)) - 4 + 50)
      .text((d) => (parseFloat(d.amount) > 0 ? `${parseFloat(d.amount).toFixed(2)}` : ""))
      .attr("font-size", 8);
  }, [results, translate]);

  return (
    <div className="barChartContainer">
      <div className="barChart" id="payment-estimations-graph"></div>
    </div>
  );
};
